"use client";

import { Heading, Icon, Text } from "@afa-shared/afa-components";
import { StandardLink } from "@components/Link/StandardLink";
import Message from "@components/Message/Message";
import RichTextRenderer from "@components/RichTextRenderer";
import { TIMEZONE_STOCKHOLM, nowDate, toDateInStockholmTimeZone } from "@utils/dateHelpers";
import { DeviatingOpeningHoursContext } from "contexts/DeviatingOpeningHoursContext";
import dayjs from "dayjs";
import { useContext, useEffect, useState } from "react";

import { DeviatingOpeningHours } from "types/DeviatingOpeningHours";
import { ContactUsBlockContext } from "types/context/ContactUsBlockContext";
import { Context } from "types/context/Context";

import styles from "./contactUs.module.scss";
import { ContactUsBlockType_contactUsBlock } from "./queries/contentfulTypes/ContactUsBlockType";

type ContactUsBlockProps = {
  blockData?: ContactUsBlockType_contactUsBlock;
  context?: Context;
};

function Section({
  icon,
  heading,
  text,
  allowLinkIcon,
  context,
  hideOpeningHoursLink,
  showBusyIndicator,
}: {
  icon?: string;
  heading?: string;
  text?: { json: any; links: any };
  allowLinkIcon?: boolean;
  context?: Context;
  linkTracker?: (value?: any) => void;
  hideOpeningHoursLink: boolean;
  showBusyIndicator?: boolean;
}) {
  const ContactUsContext: ContactUsBlockContext = {
    allowLinkIcon: allowLinkIcon,
  };
  context = {
    ...context,
    contactUsBlock: ContactUsContext,
  };

  return (
    <div className={styles.section}>
      {icon && (
        <div className={styles.iconContainer}>
          <Icon size="sm" name={icon as any} />
        </div>
      )}
      <div className={styles.dataContainer}>
        {heading && (
          <Heading variant="h4" removeMargin className={styles.sectionHeading}>
            {heading}
          </Heading>
        )}
        {text && <RichTextRenderer json={text?.json} links={text?.links} context={context} />}
        {!hideOpeningHoursLink && (
          <StandardLink
            href={"/kundtjanst/avvikande-oppettider"}
            text={"Avvikande öppettider"}
            hideIcon={true}
            trackingData={{
              sectionTitle: context?.sectionBlock?.sectionBlockHeading ?? undefined,
              sectionBlockIndex: context?.sectionBlock?.sectionBlockIndex ?? undefined,
              linkType: "contact",
            }}
          />
        )}
        {showBusyIndicator && (
          <div className={styles.busyContainer}>
            <span className={styles.busyIndicator} />
            <span className={styles.busyText}>Just nu är det många som ringer</span>
          </div>
        )}
      </div>
    </div>
  );
}

export function getDeviatingOpeningHoursState(
  deviatingOpeningHours: DeviatingOpeningHours[],
  dayJsNow: dayjs.Dayjs,
): "soon" | "today" | null {
  const dayJsDeviatingOpeningHours = deviatingOpeningHours?.map((deviatingOpeningHours) => {
    let date = dayjs(deviatingOpeningHours.date).tz(TIMEZONE_STOCKHOLM);
    if (deviatingOpeningHours.startTime) {
      const [hours, minutes] = deviatingOpeningHours.startTime.split(":");
      date = date.set("hours", parseInt(hours)).set("minutes", parseInt(minutes));
    }
    return date;
  });

  dayJsDeviatingOpeningHours?.sort((a, b) => a.unix() - b.unix());

  const dayJsNearestUpcomingDate = dayJsDeviatingOpeningHours?.find(
    (date) =>
      date.year() > dayJsNow.year() ||
      (date.year() === dayJsNow.year() && date.month() > dayJsNow.month()) ||
      (date.year() === dayJsNow.year() &&
        date.month() === dayJsNow.month() &&
        date.date() >= dayJsNow.date()),
  );
  if (!dayJsNearestUpcomingDate) {
    return null;
  }
  if (dayJsNearestUpcomingDate.isSame(dayJsNow, "day")) {
    return "today";
  }
  const threeDaysInSeconds = 60 * 60 * 24 * 3;
  if (dayJsNearestUpcomingDate.unix() - dayJsNow.unix() <= threeDaysInSeconds) {
    return "soon";
  }
  return null;
}

export function shouldShowBusyIndicator(
  now: Date,
  deviatingOpeningHours: DeviatingOpeningHours[],
): boolean {
  const stockholmNow = dayjs(now).tz(TIMEZONE_STOCKHOLM);

  // 0-6, sunday is 0
  if (stockholmNow.day() === 6 || stockholmNow.day() === 0) {
    return false; // Never show on weekends
  }
  if (stockholmNow.hour() < 11) {
    return false; // Never show before 11am
  }
  if (stockholmNow.hour() >= 13) {
    return false; // Never show after 1pm
  }

  // Check if there's a deviating opening hour right now
  for (const deviatingOpeningHour of deviatingOpeningHours) {
    const stockholmDeviatingOpeningHourDate = dayjs(deviatingOpeningHour.date).tz(
      TIMEZONE_STOCKHOLM,
    );

    if (!stockholmNow.isSame(stockholmDeviatingOpeningHourDate, "day")) {
      continue;
    }

    const stockholmNowHours = String(stockholmNow.hour()).padStart(2, "0");
    const stockholmNowMinutes = String(stockholmNow.minute()).padStart(2, "0");
    const stockholmNowTime = `${stockholmNowHours}:${stockholmNowMinutes}`;

    if (
      stockholmNowTime >= deviatingOpeningHour.startTime &&
      (deviatingOpeningHour.endTime === "00:00" || stockholmNowTime < deviatingOpeningHour.endTime)
    ) {
      return false; // Don't show if we're currently closed
    }
  }

  return true;
}

function MessageComponent({
  blockData,
  deviatingOpeningHoursState,
  showOverrideMessage,
  context,
}: {
  blockData: ContactUsBlockType_contactUsBlock;
  deviatingOpeningHoursState: ReturnType<typeof getDeviatingOpeningHoursState>;
  showOverrideMessage: boolean;
  context: Context;
}) {
  let heading = "";
  if (showOverrideMessage) {
    heading = blockData.deviatingOpeningHoursHeading;
  }
  if (!heading && deviatingOpeningHoursState === "today") {
    heading = "Idag har vi stängt";
  } else if (!heading && deviatingOpeningHoursState === "soon") {
    heading = "Avvikande öppettider";
  }

  return (
    <Message heading={heading} type="info">
      {showOverrideMessage && (
        <Text
          variant={"paragraph"}
          children={blockData.deviatingOpeningHoursMessage}
          className={styles.messageText}
        />
      )}
      <StandardLink
        text={"Se våra öppettider"}
        href={"/kundtjanst/avvikande-oppettider"}
        hideIcon={true}
        trackingData={{
          sectionTitle: context?.sectionBlock?.sectionBlockHeading ?? undefined,
          sectionBlockIndex: context?.sectionBlock?.sectionBlockIndex ?? undefined,
        }}
      />
    </Message>
  );
}

export function ContactUsBlock({ blockData, context }: ContactUsBlockProps) {
  const [showBusyIndicator, setShowBusyIndicator] = useState<boolean>(false);
  const deviatingOpeningHours = useContext(DeviatingOpeningHoursContext);

  const now = nowDate();

  const showOverrideMessage =
    toDateInStockholmTimeZone(blockData.deviatingOpeningHoursStartDate)?.getTime() <
      now.getTime() &&
    toDateInStockholmTimeZone(blockData.deviatingOpeningHoursEndDate)?.getTime() > now.getTime() &&
    blockData.deviatingOpeningHoursMessage !== "";

  const deviatingOpeningHoursState = getDeviatingOpeningHoursState(
    deviatingOpeningHours,
    dayjs(now).tz(TIMEZONE_STOCKHOLM),
  );

  const showMessage =
    showOverrideMessage ||
    (blockData.showDeviatingOpeningHours &&
      (deviatingOpeningHoursState === "soon" || deviatingOpeningHoursState === "today"));

  useEffect(() => {
    switch (blockData.showBusyIndicator) {
      case null:
      case "Visa inte":
        // eslint-disable-next-line react-hooks/set-state-in-effect
        setShowBusyIndicator(false);
        break;
      case "Visa nu":
        setShowBusyIndicator(true);
        break;
      case "Visa automatiskt vardagar kl 11-13": {
        const showBusyIndicator = shouldShowBusyIndicator(nowDate(), deviatingOpeningHours);
        setShowBusyIndicator(showBusyIndicator);
        break;
      }
      default:
        console.error(`Unexpected value for shoBusyIndicator: ${blockData.showBusyIndicator}`);
        setShowBusyIndicator(false);
        break;
    }
  }, [deviatingOpeningHours, blockData.showBusyIndicator]);

  return (
    <div
      className={styles.container}
      data-contentful-entry-id={blockData.sys.id}
      data-contentful-field-id="name"
      data-blockname="kontaktaossblock"
    >
      {blockData.heading && (
        <Heading variant="h3" className={styles.heading}>
          {blockData.heading}
        </Heading>
      )}
      {(blockData.emailHeading || blockData.emailText) && (
        <Section
          icon="email"
          heading={blockData.emailHeading}
          text={blockData.emailText}
          context={context}
          hideOpeningHoursLink={true}
        />
      )}
      {(blockData.phoneHeading || blockData.phoneText) && (
        <Section
          icon="phone_square"
          heading={blockData.phoneHeading}
          text={blockData.phoneText}
          context={context}
          // linkTracker={() =>
          //   linkTracker(blockData.phoneText.toString(), blockData.phoneText.toString(), "contact")
          // }
          hideOpeningHoursLink={true}
          showBusyIndicator={showBusyIndicator}
        />
      )}
      {(blockData.phoneHoursHeading || blockData.phoneHoursText) && (
        <>
          <Section
            icon="watch_square"
            heading={blockData.phoneHoursHeading}
            text={blockData.phoneHoursText}
            hideOpeningHoursLink={showMessage}
          />
          {showMessage && (
            <MessageComponent
              blockData={blockData}
              deviatingOpeningHoursState={deviatingOpeningHoursState}
              showOverrideMessage={showOverrideMessage}
              context={context}
            />
          )}
        </>
      )}
      {(blockData.postHeading || blockData.postText) && (
        <Section
          icon="mail_square"
          heading={blockData.postHeading}
          text={blockData.postText}
          hideOpeningHoursLink={true}
        />
      )}
      {(blockData.visitorHeading || blockData.visitorText) && (
        <Section
          icon="map_square"
          heading={blockData.visitorHeading}
          text={blockData.visitorText}
          hideOpeningHoursLink={true}
          // linkTracker={() =>
          //   linkTracker(blockData.phoneText.toString(), blockData.phoneText.toString(), "contact")
          // }
        />
      )}
      {(blockData.visitorHoursHeading || blockData.visitorHoursText) && (
        <Section
          icon="watch_square"
          heading={blockData.visitorHoursHeading}
          text={blockData.visitorHoursText}
          hideOpeningHoursLink={true}
        />
      )}
      {blockData.richText && (
        <Section text={blockData.richText} allowLinkIcon={true} hideOpeningHoursLink={true} />
      )}
    </div>
  );
}
